/*
 * TAL media behaviour: audio players, outbound "listen on" clicks, click-to-load YouTube
 * embeds, and signup / contact form events.
 *
 * Analytics: every event is published to Shopify's customer-events bus with
 * Shopify.analytics.publish(). The custom pixel in pixel/tal-ga4-custom-pixel.js
 * subscribes to these names and forwards them to TAL's GA4 property. Nothing here
 * talks to Google directly, so consent is handled in one place (the pixel).
 */
(function () {
  'use strict';
  // Every TAL section includes this file; run it once per page.
  if (window.__talMedia) return;
  window.__talMedia = true;

  var DEBUG = /[?&]tal_debug=1/.test(window.location.search);

  function publish(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* never break the page for analytics */ }
    if (DEBUG && window.console) console.log('[tal]', name, data);
  }

  function fmt(sec) {
    if (!isFinite(sec) || sec < 0) sec = 0;
    var m = Math.floor(sec / 60);
    var s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* ---------- Audio players ---------- */
  var players = [];

  function initPlayer(root) {
    if (root.__talReady) return;
    root.__talReady = true;
    var audio = root.querySelector('[data-tal-audio]');
    var toggle = root.querySelector('[data-tal-toggle]');
    var seek = root.querySelector('[data-tal-seek]');
    var time = root.querySelector('[data-tal-time]');
    if (!audio || !toggle) return;

    var kind = root.getAttribute('data-kind') || 'song';
    var base = {
      content_type: kind,
      content_id: root.getAttribute('data-id') || '',
      title: root.getAttribute('data-title') || '',
      artist: root.getAttribute('data-artist') || ''
    };
    var started = false;
    var marks = { 25: false, 50: false, 75: false };
    var completed = false;

    function setPlaying(on) {
      root.classList.toggle('is-playing', on);
      toggle.setAttribute('aria-label', (on ? 'Pause ' : 'Play ') + base.title);
    }

    toggle.addEventListener('click', function () {
      if (audio.paused) {
        players.forEach(function (p) { if (p !== audio && !p.paused) p.pause(); });
        var pr = audio.play();
        if (pr && typeof pr.catch === 'function') pr.catch(function () { setPlaying(false); });
      } else {
        audio.pause();
      }
    });

    audio.addEventListener('play', function () {
      setPlaying(true);
      if (!started) {
        started = true;
        publish(kind === 'episode' ? 'tal_episode_play' : 'tal_song_play', base);
      }
    });
    audio.addEventListener('pause', function () { setPlaying(false); });
    audio.addEventListener('ended', function () {
      setPlaying(false);
      if (!completed) {
        completed = true;
        publish(kind === 'episode' ? 'tal_episode_progress' : 'tal_song_complete', Object.assign({ percent: 100 }, base));
      }
    });
    audio.addEventListener('timeupdate', function () {
      var d = audio.duration;
      if (time) time.textContent = fmt(audio.currentTime) + (isFinite(d) ? ' / ' + fmt(d) : '');
      if (!isFinite(d) || d <= 0) return;
      var pct = (audio.currentTime / d) * 100;
      if (seek && !seek.__dragging) seek.value = String(pct);
      [25, 50, 75].forEach(function (m) {
        if (!marks[m] && pct >= m) {
          marks[m] = true;
          publish(kind === 'episode' ? 'tal_episode_progress' : 'tal_song_progress', Object.assign({ percent: m }, base));
        }
      });
    });
    if (seek) {
      seek.addEventListener('pointerdown', function () { seek.__dragging = true; });
      seek.addEventListener('change', function () {
        seek.__dragging = false;
        if (isFinite(audio.duration)) audio.currentTime = (parseFloat(seek.value) / 100) * audio.duration;
      });
    }
    players.push(audio);
  }

  /* ---------- Outbound streaming / listen-on clicks ---------- */
  document.addEventListener('click', function (ev) {
    var a = ev.target.closest && ev.target.closest('[data-tal-outbound]');
    if (!a) return;
    publish('tal_streaming_click', {
      platform: a.getAttribute('data-platform') || '',
      content_type: a.getAttribute('data-kind') || '',
      title: a.getAttribute('data-title') || '',
      link_location: a.getAttribute('data-location') || '',
      link_url: a.href
    });
  });

  /* ---------- Click-to-load YouTube with play tracking ---------- */
  var ytReady = null;
  function loadYouTubeApi() {
    if (ytReady) return ytReady;
    ytReady = new Promise(function (resolve) {
      if (window.YT && window.YT.Player) return resolve(window.YT);
      var prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () { if (prev) prev(); resolve(window.YT); };
      var s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      s.async = true;
      document.head.appendChild(s);
    });
    return ytReady;
  }

  function startVideo(wrap) {
    var id = wrap.getAttribute('data-youtube-id');
    var title = wrap.getAttribute('data-title') || '';
    if (!id || wrap.__talStarted) return;
    wrap.__talStarted = true;
    var holder = document.createElement('div');
    wrap.innerHTML = '';
    wrap.appendChild(holder);
    var marks = { 25: false, 50: false, 75: false };
    var startedOnce = false;
    var timer = null;
    var base = { content_type: 'video', title: title, video_id: id };
    loadYouTubeApi().then(function (YT) {
      var player = new YT.Player(holder, {
        videoId: id,
        host: 'https://www.youtube-nocookie.com',
        playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
        events: {
          onStateChange: function (e) {
            if (e.data === YT.PlayerState.PLAYING) {
              if (!startedOnce) { startedOnce = true; publish('tal_video_start', base); }
              if (!timer) timer = setInterval(function () {
                var d = player.getDuration();
                if (!d) return;
                var pct = (player.getCurrentTime() / d) * 100;
                [25, 50, 75].forEach(function (m) {
                  if (!marks[m] && pct >= m) { marks[m] = true; publish('tal_video_progress', Object.assign({ percent: m }, base)); }
                });
              }, 1000);
            } else if (e.data === YT.PlayerState.ENDED) {
              clearInterval(timer); timer = null;
              publish('tal_video_complete', base);
            } else if (e.data === YT.PlayerState.PAUSED) {
              clearInterval(timer); timer = null;
            }
          }
        }
      });
    });
  }

  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest && ev.target.closest('[data-tal-video-start]');
    if (!btn) return;
    var wrap = btn.closest('[data-tal-video]');
    if (wrap) startVideo(wrap);
  });

  /* ---------- Email signup and contact / sponsorship forms ---------- */
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = (form.getAttribute('action') || '').toLowerCase();
    var formType = form.querySelector('input[name="form_type"]');
    var type = formType ? formType.value : '';
    if (type === 'customer' || form.hasAttribute('data-tal-signup')) {
      publish('tal_sign_up', { method: 'email', link_location: form.getAttribute('data-location') || 'unknown' });
    } else if (type === 'contact' || action.indexOf('/contact') !== -1) {
      var sel = form.querySelector('[name="contact[inquiry_type]"]');
      publish('tal_generate_lead', { inquiry_type: sel ? sel.value : 'general' });
    }
  }, true);

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-tal-player]').forEach(initPlayer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  // Theme editor: re-init when a section is added or re-rendered.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
